import { type FC, useEffect } from "react";
import { bindAction } from "interfaces";
import { fetchIntegrationsAction } from "features/Reporting/state/menu/integrationMenuThunk";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import NavigationMenuItem from "../navigationMenuItem/NavigationMenuItem";
import { RouteNames } from "enums";
import { events } from "views/reporting/constants";
import { useRtn } from "hooks/useRtn";
import { selectMenuItemsData } from "features/Reporting/state/menu/integrationMenuSlice";
import * as performanceUtils from "features/Library/Common/utils/performanceUtils";
import { useCanViewAddonsReport } from "features/Reporting/AddOns/hooks/useCanViewAddonsReport";

export interface Props extends PropsFromRedux {
  topLevelItemClick: () => void;
  expanded: boolean;
  toggleNavigation: boolean;
  hideNavigation: () => void;
}

export const ReportingNavigationMenu: FC<Props> = ({
  topLevelItemClick,
  expanded,
  toggleNavigation,
  hideNavigation,
  integrations,
  fetchIntegrations,
  accountId,
}) => {
  const isGoogleIntegrationEnabled = integrations.Google;
  const isMicrosoftIntegrationEnabled = integrations.MSGraph;
  const isBsiAccount = performanceUtils.isBsi(accountId);

  const canViewAddOnsReport = useCanViewAddonsReport(accountId);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  useRtn(events, fetchIntegrations);

  return (
    <NavigationMenuItem
      label="Reporting"
      icon="fa-chart-pie"
      path={RouteNames.reporting}
      onClick={topLevelItemClick}
      expanded={expanded}
      toggleNavigation={toggleNavigation}
    >
      <NavigationMenuItem withMobo label="Dashboard" path={"overview"} hideNavigation={hideNavigation} />
      {(isMicrosoftIntegrationEnabled || isBsiAccount) && (
        <NavigationMenuItem withMobo label="Microsoft Graph" path={"graph"} hideNavigation={hideNavigation} />
      )}
      {isGoogleIntegrationEnabled && (
        <NavigationMenuItem withMobo label="Google Graph" path={"google"} hideNavigation={hideNavigation} />
      )}
      <NavigationMenuItem
        withMobo
        label={canViewAddOnsReport ? "Packs & Add-Ons" : "Packs & Licensing"}
        path={"packs"}
        hideNavigation={hideNavigation}
      />
      <NavigationMenuItem withMobo label="People" path={"people"} hideNavigation={hideNavigation} />
      <NavigationMenuItem withMobo label="Content" path={"content"} hideNavigation={hideNavigation} />
    </NavigationMenuItem>
  );
};

const mapStateToProps = (state: RootState) => ({
  integrations: selectMenuItemsData(state),
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchIntegrations: bindAction(fetchIntegrationsAction, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ReportingNavigationMenu);
export default ConnectedComponent;
